import { useTranslation } from 'next-i18next';
import styles from './BusinessDivision.module.scss';
import { useCallback, useEffect, useState } from "react";
import { ForwardArrows } from "./ForwardArrows";
import SwiperCore, { Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { useRouter } from "next/router";
import { BusinessDivisionItem } from "./BusinessDivisionItem";
import { BusinessDivisionDesktop } from "./BusinessDivisionDesktop";

SwiperCore.use([Pagination]);

export const BusinessDivision = () => {
  const [windowWidth, setWindowWidth] = useState(null);

  useEffect(() => {
    if (!windowWidth) {
      setWindowWidth(window.innerWidth);
    }
  }, [windowWidth]);

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowWidth && windowWidth < 992 //
    ? <BusinessDivisionMobile />
    : <BusinessDivisionDesktop />;
}

const BusinessDivisionMobile = () => {
  const { t } = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);
  const [journeyInfo, setJourneyInfo] = useState({
    title: t('businessDivision.accelerationTitle'),
    subtitle: t('businessDivision.accelerationText'),
    href: 'acceleration'
  });

  useEffect(() => {
    if (activeSlide === 0) {
      setJourneyInfo(getJourneyInfo('acceleration'));
    } else if (activeSlide === 1) {
      setJourneyInfo(getJourneyInfo('disruption'));
    } else if (activeSlide === 2) {
      setJourneyInfo(getJourneyInfo('expansion'));
    }
  }, [activeSlide, getJourneyInfo]);

  const getJourneyInfo = useCallback((state) => {
    return {
      title: t(`businessDivision.${state}Title`),
      title2: t(`businessDivision.${state}Title2`),
      subtitle: t(`businessDivision.${state}Text`),
      href: state
    };
  }, [t]);

  const journeySlide = ({
    key,
    className,
    image,
    text,
    arrowColor,
  }) => {
    return (
      <SwiperSlide key={key}>
        <div className={`${styles.jouney_slide} ${className}`}>
          <img
            src={image}
            alt={text}
          />
          <div className={styles.timeline_horizontal_line} />
          <div className={styles.timeline_vertical_mark} />
          <div className={styles.timeline_text_and_arrow}>
            <p className={styles.timeline_text}>
              {text}
            </p>
            <ForwardArrows fill={arrowColor} />
          </div>
        </div>
      </SwiperSlide>
    );
  }

  const carouselPagination = () => {
    return (
      <div className={styles.carousel_pagination}>
        <div className={styles.dots_pagination_wrapper}>
          <div className={`${styles.dot} ${activeSlide !== 0 ? styles.dot_inactive : ''}`} />
          <div className={`${styles.dot} ${activeSlide !== 1 ? styles.dot_inactive : ''}`} />
          <div className={`${styles.dot} ${activeSlide !== 2 ? styles.dot_inactive : ''}`} />
        </div>
      </div>
    )
  }

  const router = useRouter();
  const handleClickDivisionDetailsButton = (division) => {
    router.push(`/${router.query.locale}/business-division/${division}`);
  }

  const handleTextWithBr = (str) => {
    if (!journeyInfo.title.includes("<br/>")) {
      return journeyInfo.title;
    }

    let final = '';
    const parts = journeyInfo.title.split("<br/>");
    for (const p of parts) {
      final += p;
      final += <br />;
    };

    return final;
  }

  return (
    <div className={styles.container} id="who-we-are">
      <div className={styles.header_wrapper}>
        <div className={styles.line}></div>
        <div className={styles.title}>{t('businessDivision.title')}</div>
        <div className={styles.text}>{t('businessDivision.text')}</div>
      </div>
      <Swiper
        modules={[Pagination]}
        slidesPerView={1}
        loop
        className={styles.custom_swiper}
        initialSlide={0}
        scrollbar={{ draggable: true }}
        onRealIndexChange={({ realIndex }) => {
          setActiveSlide(realIndex);
        }}
      >
        {journeySlide({
          key: 0,
          className: styles.journey_slide_acceleration,
          image: '/landings/assets/images/business_division/acceleration.svg',
          text: t('businessDivision.acceleration'),
          arrowColor: '#9FE870',
        })}
        {journeySlide({
          key: 1,
          className: styles.journey_slide_disruption,
          image: '/landings/assets/images/business_division/disruption.svg',
          text: t('businessDivision.disruption'),
          arrowColor: '#0AE1FF',
        })}
        {journeySlide({
          key: 2,
          className: styles.journey_slide_expansion,
          image: '/landings/assets/images/business_division/expansion.svg',
          text: t('businessDivision.expansion'),
          arrowColor: '#7D72FF',
        })}
      </Swiper>
      {carouselPagination()}
      <p className={styles.journeyTitle}>
        {journeyInfo.title}
      </p>
      {journeyInfo.title2 &&
        <p className={styles.journeyTitle + ' ' + styles.second}>
          {journeyInfo.title2}
        </p>
      }
      <p className={styles.journeyText}>
        {journeyInfo.subtitle}
      </p>
      <button className={styles.button + ' ' + styles[journeyInfo.href]} onClick={() => handleClickDivisionDetailsButton(journeyInfo.href)}>
        {t('businessDivision.knowMore')}
      </button>
    </div>
  );
}